import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Pagination,
  Paper,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Caretaker } from '../services/caretakerSearchService';
import { useCaretakerSearch } from '../hooks/useCaretakerSearch';
import { useDiscoveryViewMode } from '../hooks/useDiscoveryViewMode';
import CaretakerSearchForm from '../components/CaretakerSearchForm';
import CaretakerFilters from '../components/CaretakerFilters';
import CaretakerCard from '../components/CaretakerCard';
import CaretakerFaceTile from '../components/CaretakerFaceTile';
import DiscoveryViewToggle from '../components/DiscoveryViewToggle';
import BlurGate from '../components/discovery/BlurGate';
import { GUEST_VISIBLE_TILE_LIMIT } from '../constants/guestDiscovery';
import { useApp } from '../context/AppContext';
import { authService } from '../services/authService';

export interface SearchCaretakersPageProps {
  /** When true, applies MVP-18.1 guest blur gate (first 10 tiles visible). */
  guestMode?: boolean;
}

const FACE_GRID_BREAKPOINTS = { xs: 4, sm: 3, md: 2, lg: 2 } as const;
const CARD_GRID_BREAKPOINTS = { xs: 12, sm: 6, md: 4, lg: 3 } as const;

const SearchCaretakersPage: React.FC<SearchCaretakersPageProps> = ({ guestMode = false }) => {
  const navigate = useNavigate();
  const { state, dispatch } = useApp();
  const { viewMode, setViewMode } = useDiscoveryViewMode();

  const isGuest =
    guestMode || !authService.isAuthenticated() || !state.user;

  const requireAuth = () => {
    dispatch({
      type: 'SET_REDIRECT_URL',
      payload: window.location.pathname + window.location.search,
    });
    navigate('/auth');
  };

  const {
    caretakers,
    specialties,
    loading,
    error,
    filters,
    currentPage,
    totalPages,
    totalResults,
    setFilters,
    clearFilters,
    search,
    getCaretakerSuggestions,
    getSpecialtySuggestions,
  } = useCaretakerSearch();

  const handleFiltersChange: typeof setFilters = (newFilters) => {
    if (isGuest) {
      requireAuth();
      return;
    }
    setFilters(newFilters);
  };

  const handleSearch = () => {
    if (isGuest) {
      requireAuth();
      return;
    }
    search(1, false);
  };

  const handleCaretakerClick = (caretaker: Caretaker) => {
    if (isGuest) {
      requireAuth();
      return;
    }
    navigate(`/caretaker/${caretaker.id}`);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    if (isGuest) {
      requireAuth();
      return;
    }
    search(page, false);
  };

  const visibleCaretakers = isGuest
    ? caretakers.slice(0, GUEST_VISIBLE_TILE_LIMIT)
    : caretakers;
  const blurredCaretakers = isGuest
    ? caretakers.slice(GUEST_VISIBLE_TILE_LIMIT)
    : [];

  const gridBreakpoints = viewMode === 'faces' ? FACE_GRID_BREAKPOINTS : CARD_GRID_BREAKPOINTS;

  const renderCaretakerTile = (caretaker: Caretaker, blurred = false) => {
    const tile =
      viewMode === 'faces' ? (
        <CaretakerFaceTile
          caretaker={caretaker}
          onPress={() => handleCaretakerClick(caretaker)}
        />
      ) : (
        <CaretakerCard caretaker={caretaker} onViewProfile={handleCaretakerClick} />
      );

    if (!blurred) {
      return tile;
    }

    return (
      <BlurGate gated testId={`discovery-blur-gate-${caretaker.id}`}>
        {tile}
      </BlurGate>
    );
  };

  const renderResults = () => {
    if (loading && caretakers.length === 0) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress size={40} />
          <Typography variant="body1" sx={{ ml: 2 }}>
            Searching for caretakers...
          </Typography>
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Search Error
          </Typography>
          <Typography variant="body2">
            {error}
          </Typography>
          <Button
            variant="outlined"
            onClick={() => (isGuest ? requireAuth() : search(1, false))}
            sx={{ mt: 2 }}
          >
            Try Again
          </Button>
        </Alert>
      );
    }

    if (caretakers.length === 0) {
      return (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            No caretakers found
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            We couldn&apos;t find any caretakers matching your search criteria.
          </Typography>
          {!isGuest && (
            <Button variant="contained" onClick={clearFilters} sx={{ mt: 3 }}>
              Clear All Filters
            </Button>
          )}
        </Paper>
      );
    }

    return (
      <>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            {totalResults} caretaker{totalResults !== 1 ? 's' : ''} found
            {isGuest ? ` (showing first ${GUEST_VISIBLE_TILE_LIMIT})` : ''}
          </Typography>
          <DiscoveryViewToggle viewMode={viewMode} onChange={setViewMode} />
        </Box>

        <Grid
          container
          spacing={viewMode === 'faces' ? 2 : 3}
          data-testid={
            viewMode === 'faces' ? 'discovery-results-faces' : 'discovery-results-grid'
          }
        >
          {visibleCaretakers.map((caretaker) => (
            <Grid item {...gridBreakpoints} key={caretaker.id}>
              {renderCaretakerTile(caretaker)}
            </Grid>
          ))}
          {blurredCaretakers.map((caretaker) => (
            <Grid item {...gridBreakpoints} key={`blurred-${caretaker.id}`}>
              {renderCaretakerTile(caretaker, true)}
            </Grid>
          ))}
        </Grid>

        {loading && caretakers.length > 0 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <CircularProgress />
          </Box>
        )}

        {!isGuest && totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={4}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="large"
            />
          </Box>
        )}

        {isGuest && blurredCaretakers.length > 0 && (
          <Box textAlign="center" mt={3}>
            <Button variant="contained" onClick={requireAuth}>
              Sign in to see all caregivers
            </Button>
          </Box>
        )}
      </>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {isGuest && (
        <Alert severity="info" sx={{ mb: 2 }} data-testid="guest-discovery-banner">
          Browse the first {GUEST_VISIBLE_TILE_LIMIT} caregivers without an account. Sign in to
          filter, view profiles, and book care.
        </Alert>
      )}

      <CaretakerSearchForm
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onSearch={handleSearch}
        loading={loading}
        getCaretakerSuggestions={getCaretakerSuggestions}
        getSpecialtySuggestions={getSpecialtySuggestions}
      />

      <CaretakerFilters
        filters={filters}
        specialties={specialties}
        onFiltersChange={handleFiltersChange}
        loading={loading}
      />

      <Divider sx={{ my: 3 }} />

      {renderResults()}
    </Container>
  );
};

export default SearchCaretakersPage;
